import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { integrationDemandLinkKeys, solutionWorkloadKeys } from "@/lib/query";

export type DemandLink = {
  id: string;
  nymblDemandId: string;
  demandName: string | null;
  linkedAt: string;
  lastSyncedAt: string | null;
};

export type SolutionDemandLinksGroup = {
  solutionId: string;
  solutionName: string;
  accountName: string;
  links: DemandLink[];
};

export type DemandLinksResponse = {
  solutions: SolutionDemandLinksGroup[];
};

export type RefreshDemandLinksResult = {
  fetched: number;
  created: number;
  updated: number;
  unmapped: number;
  pruned: number;
  errors: number;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useDemandLinks() {
  return useQuery({
    queryKey: integrationDemandLinkKeys.list(),
    queryFn: () => jsonFetch<DemandLinksResponse>("/api/integrations/demand-links"),
  });
}

export function useRefreshDemandLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshDemandLinksResult>("/api/integrations/demand-links/refresh", {
        method: "POST",
      }),
    onSuccess: (r) => {
      // Re-scoping the links changes both the coverage view and every Solution's
      // guidance/actuals, so invalidate the workload reads too.
      qc.invalidateQueries({ queryKey: integrationDemandLinkKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      const unmapped = r.unmapped > 0 ? `, ${r.unmapped} unmapped` : "";
      toast.success(
        `Demand links synced — ${r.created} added, ${r.updated} updated, ${r.pruned} removed${unmapped}`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
